import React from 'react';
import { Container, Typography, Paper, Box, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';

export default function GraphqlBasics3Docs() {
  const navigate = useNavigate();

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h3" component="h1" gutterBottom>
          GraphQL Basics - Part 3
        </Typography>
        <Button variant="outlined" onClick={() => navigate('/')}>
          🏠 HOME
        </Button>
      </Box>

      <Typography variant="body1" sx={{ mb: 3 }}>
        실시간 데이터 처리와 성능 최적화 방법을 학습합니다. 
        Subscription을 활용한 실시간 기능부터 고급 캐싱 전략까지 실무에 필요한 기법들을 다룹니다.
      </Typography>

      <Typography variant="h4" gutterBottom sx={{ mt: 4, mb: 2 }}>
        실시간 데이터와 최적화
      </Typography>

      <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2 }}>
        Subscription으로 실시간 업데이트
      </Typography>
      <Typography variant="body2" sx={{ mb: 2 }}>
        GraphQL Subscription을 사용하여 실시간으로 데이터 변경사항을 받을 수 있습니다. 
        채팅, 알림, 재고 변동 등에 활용됩니다.
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50', mb: 3 }}>
        <Typography component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.875rem', margin: 0 }}>
{`import { useSubscription, gql } from '@apollo/client';

const PRODUCT_UPDATED = gql\`
  subscription ProductUpdated {
    productUpdated {
      id
      name
      price
      inventory {
        quantity
        inStock
      }
    }
  }
\`;

function RealTimeProductMonitor() {
  const [notifications, setNotifications] = useState([]);
  
  const { data, loading, error } = useSubscription(PRODUCT_UPDATED);

  useEffect(() => {
    if (data?.productUpdated) {
      const product = data.productUpdated;
      
      if (!product.inventory.inStock) {
        const notification = {
          id: Date.now(),
          message: \`\${product.name} 상품이 품절되었습니다!\`,
          timestamp: new Date()
        };
        
        setNotifications(prev => [notification, ...prev.slice(0, 4)]);
      }
    }
  }, [data]);

  return (
    <Box>
      <Typography variant="h6">실시간 상품 모니터링</Typography>
      
      {data && (
        <Card sx={{ mb: 2, bgcolor: 'info.light' }}>
          <Typography>{data.productUpdated.name}</Typography>
          <Typography>가격: ₩{data.productUpdated.price}</Typography>
          <Chip 
            label={data.productUpdated.inStock ? '재고 있음' : '품절'} 
            color={data.productUpdated.inStock ? 'success' : 'error'}
          />
        </Card>
      )}
    </Box>
  );
}`}
        </Typography>
      </Paper>

      <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2 }}>
        성능 최적화 기법
      </Typography>
      <Typography variant="body2" sx={{ mb: 2 }}>
        Apollo Client의 고급 캐싱과 최적화 기능을 활용하는 방법입니다.
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50', mb: 3 }}>
        <Typography component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.875rem', margin: 0 }}>
{`// 캐시 정책 설정
const client = new ApolloClient({
  cache: new InMemoryCache({
    typePolicies: {
      Product: {
        fields: {
          reviews: {
            merge(existing = [], incoming, { args }) {
              if (args?.offset === 0) {
                return incoming;  // 새로고침
              }
              return [...existing, ...incoming];  // 페이지네이션
            }
          }
        }
      }
    }
  })
});

// Optimistic Updates
const [updateProduct] = useMutation(UPDATE_PRODUCT, {
  optimisticResponse: (vars) => ({
    updateProduct: {
      __typename: 'Product',
      id: vars.id,
      ...vars.input,
      updatedAt: new Date().toISOString()
    }
  }),
  
  onError: (error) => {
    toast.error('업데이트 실패: ' + error.message);
  }
});`}
        </Typography>
      </Paper>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
        <Button 
          variant="outlined" 
          onClick={() => navigate('/docs/graphql-basics-2')}
        >
          ← PART 2: Apollo Client 설정
        </Button>
        <Button 
          variant="contained" 
          onClick={() => navigate('/docs/full-integration-1')}
        >
          다음: Full Integration →
        </Button>
      </Box>
    </Container>
  );
}
